import pick from 'lodash/pick';

import { AskAiConcept } from '@lumapps/ask-ai-concepts/types';

import type {
    AskAiRuleAssociatedConcept,
    AskAiRule,
    AskAiRuleFormOutput,
    CreateOrUpdateRuleDTO,
    RuleAnswer,
    RuleAnswerCustomTypeOutputDTO,
    RuleAnswerExternalSourcesOutputDTO,
    RuleAnswerToolsAgentOutputDTO,
    RuleAnswerOutputDTO,
    RulePutDTO,
    RuleAnswerExternalSourcesType,
} from './types';
import { RuleAnswerType } from './types'; // Imported separately because it can't be imported as a type (value export)

export const isExistingRule = (rule: CreateOrUpdateRuleDTO): rule is RulePutDTO => Boolean(rule.id);

/**
 * Format the answer to match the output expected by the backend.
 */
export const formatRuleAnswerOutput = (ruleAnswer: RuleAnswer): RuleAnswerOutputDTO => {
    switch (ruleAnswer.type) {
        case RuleAnswerType.Custom: {
            const customAnswerFields: RuleAnswerCustomTypeOutputDTO = pick(ruleAnswer, [
                'type',
                'message',
                'actionableItem.payload.id',
                'actionableItem.type',
            ]);

            return customAnswerFields;
        }
        case RuleAnswerType.ExternalSources: {
            const externalSourceAnswerFields = pick(ruleAnswer, ['type']);

            const externalSourceAnswer: RuleAnswerExternalSourcesOutputDTO = {
                ...externalSourceAnswerFields,
                sourceIds: ruleAnswer.sources?.map((s) => s.id) || [],
            };

            return externalSourceAnswer;
        }
        case RuleAnswerType.ToolsAgent: {
            const toolsAgentAnswer: RuleAnswerToolsAgentOutputDTO = {
                type: RuleAnswerType.ToolsAgent,
                toolIds: ruleAnswer.tools?.map((t) => t.id) || [],
                sourceIds: ruleAnswer.sources?.map((s) => s.id) || [],
            };

            return toolsAgentAnswer;
        }
        default:
            return {};
    }
};

/**
 * Format the form rule to match the output expected by the backend
 */
export const formatRuleOutput = (rule: AskAiRuleFormOutput): CreateOrUpdateRuleDTO => {
    const { concepts, ...restRule } = rule;
    // reformat certain fields
    return {
        ...restRule,
        groups: rule.groups?.map((group) => group.id),
        answer: formatRuleAnswerOutput(rule.answer),
        query: {
            or: concepts.map((condition) => ({
                concepts: condition.concepts.map((concept: AskAiConcept) => concept.id),
            })),
        },
    };
};

/**
 * Formats an Ask AI Rule into an array of concepts suitable for useFieldArray default values.

 * @param {AskAiRule} rule - The Ask Ai Rule to format
 * @returns {FormattedConceptsToFieldArray} - The formatted array of concepts.
 */
export const formatRuleConceptsToFieldArray = (rule?: AskAiRule) => {
    if (!rule || rule?.query?.or?.length === 0) {
        return [
            {
                concepts: [],
            },
        ];
    }
    const { concepts: detailedConcepts, query } = rule;

    const formattedQuery = query.or.map((condition) => {
        // Map into rule.query.or to get all or-conditions and replace their concepts' ids with the full detailedConcepts
        const formattedConcepts = condition.concepts.map((conceptId: string) =>
            detailedConcepts.find((concept) => concept.id === conceptId),
        );
        // As Array.find() will return a matching concept or undefined, we filter out all the undefined from the initial result
        const cleanedFormattedConcepts = formattedConcepts.filter(
            (conceptOrUndefined): conceptOrUndefined is AskAiRuleAssociatedConcept => conceptOrUndefined !== undefined,
        );
        return { concepts: cleanedFormattedConcepts };
    });

    return formattedQuery;
};

/**
 * Rules answers based on sources that were created before the AAI-839 rework all contain the `sources` attribute regardless of `type`.
 * We type guard as followed, to handle the case of pre-existing `assistant_source` type answers (considering them as `external_sources`)
 * In a further PR, after a backend migration has been done, this will be deleted.
 */
export const answerHasSources = (answer: RuleAnswer): answer is RuleAnswerExternalSourcesType => 'sources' in answer;

/**
 * Sets the default answer of the rules creation/edition form
 */
export const setDefaultAnswer = (routerData?: RuleAnswer, defaultRule?: AskAiRule) => {
    if (routerData && !defaultRule) {
        /** Use data retrieved from the router */
        return routerData;
    }
    if (defaultRule) {
        /**
         * Use the fetched defaultRule data
         * Set type to `ExternalSources` if the answer has `sources` attribute to reformat existing assistant type sources
         */
        return {
            ...defaultRule?.answer,
            type: answerHasSources(defaultRule.answer) ? RuleAnswerType.ExternalSources : RuleAnswerType.Custom,
        };
    }
    /** Default behaviour */
    return { type: RuleAnswerType.ExternalSources };
};
